import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Search, Users, Plane, CheckSquare, AlertCircle, ChevronRight, Trash2, Loader2, Bell, Filter, Settings, Link2, Calendar, Clock, Sun, Moon, MessageSquare, ArrowUpDown, LayoutGrid, List, ExternalLink } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TripDetail } from './TripDetail';
import { NotificationBell } from '../components/NotificationBell';
import { VTCommandCenter, HealthSummaryStrip } from '../components/VTCommandCenter';
import { VTGlobalSearch } from '../components/VTGlobalSearch';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  lead: 'bg-[#8C8C8C]/10 text-[#8C8C8C]',
  active: 'bg-[#C5A059]/10 text-[#C5A059]',
  traveling: 'bg-[#8DA399]/10 text-[#8DA399]',
  completed: 'bg-[#2C2C2C]/10 text-[#2C2C2C]',
  planning: 'bg-[#8C8C8C]/10 text-[#8C8C8C]',
  review: 'bg-[#C5A059]/10 text-[#C5A059]',
  booking: 'bg-[#B37E6B]/10 text-[#B37E6B]',
  finalized: 'bg-[#8DA399]/10 text-[#8DA399]',
  complete: 'bg-[#2C2C2C]/10 text-[#2C2C2C]',
};

const TRIP_STATUS_COLORS = {
  draft: 'bg-[#8C8C8C]/10 text-[#8C8C8C]',
  changes_requested: 'bg-[#B37E6B]/10 text-[#B37E6B]',
  approved: 'bg-[#C5A059]/10 text-[#C5A059]',
  booking_in_progress: 'bg-[#8DA399]/10 text-[#8DA399]',
  booked: 'bg-[#2C2C2C]/10 text-[#2C2C2C]',
};

const TRIP_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'anniversary', label: 'Anniversary' },
  { value: 'group', label: 'Group' },
  { value: 'cruise', label: 'Cruise' },
  { value: 'family', label: 'Family' },
  { value: 'honeymoon', label: 'Honeymoon' },
  { value: 'solo', label: 'Solo' },
  { value: 'business', label: 'Business' },
  { value: 'wellness', label: 'Wellness' },
];

const TRIP_STATUS_FILTER = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'changes_requested', label: 'Changes Requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'booking_in_progress', label: 'Booking In Progress' },
  { value: 'booked', label: 'Booked' },
];

export function VeraultTravels() {
  const { themeMode, setThemeMode, currentTheme } = useTheme();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState('dashboard');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [newAction, setNewAction] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTripType, setFilterTripType] = useState('all');
  // Templates/Settings
  const [showSettings, setShowSettings] = useState(false);
  const [vtSettings, setVtSettings] = useState({
    logo_url: '',
    company_name: 'VÉRAULT TRAVELS',
    footer_text: 'Prepared by VÉRAULT TRAVELS',
    default_contract_url: '',
    default_terms_url: '',
    default_card_auth_url: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [calendarData, setCalendarData] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [pendingCommsCount, setPendingCommsCount] = useState(0);

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, clientsRes, settingsRes, alertsRes, commsRes] = await Promise.all([
        axios.get(`${API}/travels/dashboard`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/travels/settings`),
        axios.get(`${API}/advisor/alerts`),
        axios.get(`${API}/communications?status=awaiting_reply`).catch(() => ({ data: [] }))
      ]);
      setDashboard(dashRes.data);
      setAllClients(clientsRes.data || []);
      if (settingsRes.data) {
        setVtSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
      if (alertsRes.data) {
        setAlerts(alertsRes.data.alerts || []);
      }
      const comms = commsRes.data;
      setPendingCommsCount(Array.isArray(comms) ? comms.length : (comms?.communications?.length || 0));
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Fetch calendar data when view changes to calendar
  const fetchCalendar = useCallback(async () => {
    setCalendarLoading(true);
    try {
      const res = await axios.get(`${API}/travels/calendar`);
      setCalendarData(res.data.entries || []);
    } catch (error) {
      console.error('Error fetching calendar:', error);
      toast.error('Failed to load calendar');
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check URL params for calendar view
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') === 'calendar') {
      setView('calendar');
      fetchCalendar();
    }
  }, [fetchCalendar]);

  // HARDENED: Add action with proper feedback
  const addAction = async (e) => {
    e.preventDefault();
    if (!newAction.trim()) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API}/travels/actions`, { text: newAction.trim() });
      setNewAction('');
      await fetchDashboard();
      toast.success('Action added');
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Failed to add action';
      console.error('Error adding action:', msg);
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  // HARDENED: Toggle action with proper feedback
  const toggleAction = async (id, completed) => {
    try {
      await axios.patch(`${API}/travels/actions/${id}?completed=${!completed}`);
      await fetchDashboard();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Failed to update action';
      console.error('Error updating action:', msg);
      toast.error(msg);
    }
  };

  // HARDENED: Delete action with proper feedback
  const deleteAction = async (id) => {
    try {
      await axios.delete(`${API}/travels/actions/${id}`);
      await fetchDashboard();
      toast.success('Action deleted');
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Failed to delete action';
      console.error('Error deleting action:', msg);
      toast.error(msg);
    }
  };

  // Save VT settings (branding + templates)
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.patch(`${API}/travels/settings`, vtSettings);
      toast.success('Settings saved');
      setShowSettings(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleClientClick = (client) => {
    navigate(`/advisor/manage/clients/${client.id}`);
  };

  const handleTripClick = (trip) => {
    // Use URL-based navigation for refresh persistence
    window.location.href = `/advisor/trips/${trip.id}`;
  };

  // HARDENED: Open trip from change request
  const handleChangeRequestClick = async (request) => {
    if (!request.trip_id) {
      toast.error('Change request has no linked trip');
      return;
    }
    // Use URL-based navigation for refresh persistence
    window.location.href = `/advisor/trips/${request.trip_id}`;
  };

  // Navigate to trip from notification
  const handleNotificationClick = (notification) => {
    if (notification.trip_id) {
      window.location.href = `/advisor/trips/${notification.trip_id}`;
    }
  };

  const handleNewClient = () => {
    navigate('/advisor/manage/clients');
  };

  // B1: New Trip with immediate loading state - FIXED: Instant response
  const [creatingTrip, setCreatingTrip] = useState(false);
  
  const handleNewTripWithLoading = (prelinkedClient = null) => {
    // INSTANT - No async, no delay, direct navigation
    if (prelinkedClient) {
      setSelectedTrip({
        client_id: prelinkedClient.id,
        client_name: prelinkedClient.full_name,
        destinations: '',
        start_date: '',
        end_date: ''
      });
    } else {
      setSelectedTrip(null);
    }
    setView('trip-detail');
  };

  const handleNewTrip = (prelinkedClient = null) => {
    if (prelinkedClient) {
      setSelectedTrip({
        client_id: prelinkedClient.id,
        client_name: prelinkedClient.full_name,
        destinations: '',
        start_date: '',
        end_date: ''
      });
    } else {
      setSelectedTrip(null);
    }
    setView('trip-detail');
  };

  const handleBackToDashboard = () => {
    setView('dashboard');
    setSelectedTrip(null);
    fetchDashboard();
  };

  const handleTripSave = () => {
    fetchDashboard();
  };

  const filteredClients = dashboard?.active_clients?.filter(c =>
    c.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Filter trips by status and type
  const filteredTrips = useMemo(() => {
    const allTrips = dashboard?.trips_in_progress || [];
    return allTrips.filter(trip => {
      const matchStatus = filterStatus === 'all' || trip.trip_status === filterStatus || trip.status === filterStatus;
      const matchType = filterTripType === 'all' || (trip.trip_types || []).includes(filterTripType);
      return matchStatus && matchType;
    });
  }, [dashboard?.trips_in_progress, filterStatus, filterTripType]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  if (view === 'trip-detail') {
    return (
      <TripDetail 
        trip={selectedTrip}
        clients={allClients}
        onBack={handleBackToDashboard}
        onSave={handleTripSave}
      />
    );
  }

  // Calendar View - Global overview of all trips
  if (view === 'calendar') {
    return (
      <div data-testid="calendar-view" className="min-h-screen p-6 md:p-12 lg:p-16 bg-[#F5F1EB]">
        <Toaster position="top-right" richColors />
        
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <button
            onClick={() => setView('dashboard')}
            className="flex items-center gap-2 text-[#8C8A86] hover:text-[#1A1A1A] transition-colors mb-4 font-body text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-3xl text-[#1A1A1A]">Trip Calendar</h1>
              <p className="text-[#8C8A86] font-body mt-1">All upcoming and past departures</p>
            </div>
          </div>
        </motion.div>

        {/* Calendar Entries */}
        {calendarLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#C8A85A]" />
          </div>
        ) : calendarData.length === 0 ? (
          <div className="text-center py-12 text-[#8C8A86]">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p>No trips with dates found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Upcoming Trips */}
            <div>
              <h2 className="font-serif text-lg text-[#1A1A1A] mb-3">Upcoming Departures</h2>
              <div className="grid gap-3">
                {calendarData
                  .filter(trip => trip.days_until_departure !== null && trip.days_until_departure >= 0)
                  .sort((a, b) => a.days_until_departure - b.days_until_departure)
                  .map(trip => (
                    <motion.div
                      key={trip.trip_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => {
                        setSelectedTrip({ id: trip.trip_id });
                        setView('trip-detail');
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-center min-w-[60px]">
                            <p className="text-2xl font-serif text-[#C8A85A]">{trip.days_until_departure}</p>
                            <p className="text-xs text-[#8C8A86] uppercase">days</p>
                          </div>
                          <div>
                            <p className="font-medium text-[#1A1A1A]">{trip.trip_title}</p>
                            <p className="text-sm text-[#8C8A86]">{trip.destination}</p>
                            {trip.primary_client && (
                              <p className="text-xs text-[#8C8A86] mt-1">{trip.primary_client}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`inline-block px-2 py-1 rounded-full text-xs ${
                            trip.lifecycle_stage === 'booked' ? 'bg-[#8DA399]/10 text-[#8DA399]' :
                            trip.lifecycle_stage === 'in_travel' ? 'bg-[#1A1A1A]/10 text-[#1A1A1A]' :
                            'bg-[#C8A85A]/10 text-[#C8A85A]'
                          }`}>
                            {trip.lifecycle_label}
                          </div>
                          <p className="text-xs text-[#8C8A86] mt-1">{trip.waiting_on_party_label}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
              </div>
            </div>

            {/* Past Trips */}
            {calendarData.filter(trip => trip.days_until_departure !== null && trip.days_until_departure < 0).length > 0 && (
              <div className="mt-8">
                <h2 className="font-serif text-lg text-[#8C8A86] mb-3">Past Departures</h2>
                <div className="grid gap-3 opacity-70">
                  {calendarData
                    .filter(trip => trip.days_until_departure !== null && trip.days_until_departure < 0)
                    .sort((a, b) => b.days_until_departure - a.days_until_departure)
                    .slice(0, 10)
                    .map(trip => (
                      <motion.div
                        key={trip.trip_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white/60 rounded-xl p-4 cursor-pointer hover:bg-white transition-colors"
                        onClick={() => {
                          setSelectedTrip({ id: trip.trip_id });
                          setView('trip-detail');
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="text-center min-w-[60px]">
                              <p className="text-lg font-serif text-[#8C8A86]">{Math.abs(trip.days_until_departure)}</p>
                              <p className="text-xs text-[#8C8A86] uppercase">days ago</p>
                            </div>
                            <div>
                              <p className="font-medium text-[#1A1A1A]">{trip.trip_title}</p>
                              <p className="text-sm text-[#8C8A86]">{trip.destination}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="inline-block px-2 py-1 rounded-full text-xs bg-[#8C8A86]/10 text-[#8C8A86]">
                              {trip.lifecycle_label}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="verault-travels-page" className="min-h-screen p-6 md:p-12 lg:p-16" style={{ backgroundColor: 'var(--vt-bg)' }}>
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        {/* Uniform Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[var(--vt-text-muted)] hover:text-[var(--vt-text)] transition-colors mb-4 font-body text-sm"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* VT HOME LOGO - Always links back to main VT dashboard */}
          <button
            onClick={handleBackToDashboard}
            className="flex items-center gap-3 group"
            data-testid="vt-home-btn"
            title="Return to Vérault Travels Dashboard"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[var(--vt-gold)] to-[var(--vt-gold-dark,#A8883A)] flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
              <Plane className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-serif text-3xl text-[var(--vt-text)] group-hover:text-[var(--vt-gold)] transition-colors">Vérault Travels</h1>
          </button>
          
          {/* GLOBAL SEARCH (MANDATORY) */}
          <div className="flex-1 max-w-lg">
            <VTGlobalSearch />
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* Theme Toggle */}
            <div className="flex items-center gap-1 bg-[var(--vt-bg-muted)] rounded-lg p-1">
              <button
                onClick={() => setThemeMode('day')}
                className={`p-2 rounded-md transition-colors ${themeMode === 'day' ? 'bg-[var(--vt-bg-card)] shadow-sm' : 'hover:bg-[var(--vt-bg-card)]/50'}`}
                title="Day Mode"
                data-testid="theme-day-btn"
              >
                <Sun className={`w-4 h-4 ${themeMode === 'day' ? 'text-[var(--vt-gold)]' : 'text-[var(--vt-text-muted)]'}`} />
              </button>
              <button
                onClick={() => setThemeMode('night')}
                className={`p-2 rounded-md transition-colors ${themeMode === 'night' ? 'bg-[var(--vt-bg-card)] shadow-sm' : 'hover:bg-[var(--vt-bg-card)]/50'}`}
                title="Night Mode"
                data-testid="theme-night-btn"
              >
                <Moon className={`w-4 h-4 ${themeMode === 'night' ? 'text-[var(--vt-gold)]' : 'text-[var(--vt-text-muted)]'}`} />
              </button>
            </div>
            <NotificationBell onNotificationClick={handleNotificationClick} />
            <Button
              onClick={() => setShowSettings(true)}
              variant="outline"
              className="border-[var(--vt-border-strong)]"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button
              data-testid="new-client-btn"
              onClick={handleNewClient}
              variant="outline"
              className="border-[var(--vt-border-strong)]"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Client
            </Button>
            {/* B1: NEW TRIP BUTTON - INSTANT response */}
            <Button
              data-testid="new-trip-btn"
              onClick={() => handleNewTripWithLoading()}
              className="bg-[var(--vt-status-bg)] text-[var(--vt-status-text)]"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Trip
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-[rgba(0,0,0,0.06)]">
              <h2 className="font-serif text-xl text-[#1A1A1A]">Settings & Templates</h2>
              <p className="text-sm text-[#8C8C8C]">Branding and default agreement URLs</p>
            </div>
            <div className="p-6 space-y-6">
              {/* Branding */}
              <div>
                <h3 className="font-medium text-[#1A1A1A] mb-3">Branding</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Logo URL</label>
                    <Input
                      value={vtSettings.logo_url || ''}
                      onChange={(e) => setVtSettings(prev => ({ ...prev, logo_url: e.target.value }))}
                      placeholder="https://your-logo.png"
                      className="bg-[#FAFAFA]"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Company Name</label>
                    <Input
                      value={vtSettings.company_name || ''}
                      onChange={(e) => setVtSettings(prev => ({ ...prev, company_name: e.target.value }))}
                      className="bg-[#FAFAFA]"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Footer Text</label>
                    <Input
                      value={vtSettings.footer_text || ''}
                      onChange={(e) => setVtSettings(prev => ({ ...prev, footer_text: e.target.value }))}
                      className="bg-[#FAFAFA]"
                    />
                  </div>
                </div>
              </div>
              
              {/* Default Templates */}
              <div>
                <h3 className="font-medium text-[#1A1A1A] mb-3">Default Agreement Templates</h3>
                <p className="text-xs text-[#8C8C8C] mb-3">
                  Set default URLs for new trips. Use &quot;Apply Templates&quot; in a trip to populate.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-[#8C8C8C] uppercase mb-1 block flex items-center gap-1">
                      <Link2 className="w-3 h-3" /> Default Contract URL
                    </label>
                    <Input
                      value={vtSettings.default_contract_url || ''}
                      onChange={(e) => setVtSettings(prev => ({ ...prev, default_contract_url: e.target.value }))}
                      placeholder="https://your-contract-form..."
                      className="bg-[#FAFAFA]"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#8C8C8C] uppercase mb-1 block flex items-center gap-1">
                      <Link2 className="w-3 h-3" /> Default Terms & Conditions URL
                    </label>
                    <Input
                      value={vtSettings.default_terms_url || ''}
                      onChange={(e) => setVtSettings(prev => ({ ...prev, default_terms_url: e.target.value }))}
                      placeholder="https://your-terms-page..."
                      className="bg-[#FAFAFA]"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#8C8C8C] uppercase mb-1 block flex items-center gap-1">
                      <Link2 className="w-3 h-3" /> Default Card Authorization URL
                    </label>
                    <Input
                      value={vtSettings.default_card_auth_url || ''}
                      onChange={(e) => setVtSettings(prev => ({ ...prev, default_card_auth_url: e.target.value }))}
                      placeholder="https://your-card-auth-form..."
                      className="bg-[#FAFAFA]"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-[rgba(0,0,0,0.06)] flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowSettings(false)}>
                Cancel
              </Button>
              <Button onClick={saveSettings} disabled={savingSettings} className="bg-[#2C2C2C] text-white">
                {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Settings
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Dashboard Cards - Clickable Navigation */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* B3: MANAGE BUTTON - Primary entry point for all management */}
        <button
          onClick={() => navigate('/advisor/manage')}
          className="bg-[var(--vt-bg-card)] rounded-xl p-5 border border-[var(--vt-border)] hover:border-[var(--vt-gold)] transition-all text-left group"
          data-testid="dash-manage-btn"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xl font-serif text-[var(--vt-text)]">Manage</p>
              <p className="text-sm text-[var(--vt-text-muted)] mt-1">Trips, Clients, Bookings</p>
            </div>
            <Settings className="w-6 h-6 text-[var(--vt-gold)] group-hover:scale-110 transition-transform" />
          </div>
        </button>
        
        {/* B5: TRIPS IN PROGRESS - Fixed deep link to trips portal */}
        <button
          onClick={() => navigate('/advisor/manage/trips')}
          className="bg-[var(--vt-bg-card)] rounded-xl p-5 border border-[var(--vt-border)] hover:border-[var(--vt-sage)] transition-all text-left group"
          data-testid="dash-trips-btn"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-serif text-[var(--vt-text)]">{dashboard?.stats?.total_trips_in_progress || 0}</p>
              <p className="text-sm text-[var(--vt-text-muted)] mt-1">Trips In Progress</p>
            </div>
            <Plane className="w-6 h-6 text-[var(--vt-sage)] group-hover:scale-110 transition-transform" />
          </div>
        </button>
        
        {/* ADDENDUM 3: Calendar Card - Prominent entry point */}
        <button
          onClick={() => { setView('calendar'); fetchCalendar(); }}
          className="bg-[var(--vt-bg-card)] rounded-xl p-5 border border-[var(--vt-border)] hover:border-[var(--vt-stone)] transition-all text-left group"
          data-testid="dash-calendar-btn"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-serif text-[var(--vt-text)]">{calendarData?.length || 0}</p>
              <p className="text-sm text-[var(--vt-text-muted)] mt-1">Calendar</p>
            </div>
            <Calendar className="w-6 h-6 text-[var(--vt-stone)] group-hover:scale-110 transition-transform" />
          </div>
        </button>
        
        {/* Communications Card */}
        <button
          onClick={() => navigate('/advisor/communications')}
          className="bg-[var(--vt-bg-card)] rounded-xl p-5 border border-[var(--vt-border)] hover:border-[var(--vt-terra)] transition-all text-left group"
          data-testid="dash-comms-btn"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-serif text-[var(--vt-text)]">{pendingCommsCount}</p>
              <p className="text-sm text-[var(--vt-text-muted)] mt-1">Pending Replies</p>
            </div>
            <MessageSquare className="w-6 h-6 text-[var(--vt-terra)] group-hover:scale-110 transition-transform" />
          </div>
        </button>
      </motion.div>

      {/* Financial Overview Strip */}
      {dashboard?.financial_overview && (dashboard.financial_overview.total_ar > 0 || dashboard.financial_overview.total_ap > 0) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
          data-testid="financial-overview-strip"
        >
          <div className="bg-[var(--vt-bg-card)] rounded-lg p-4 border border-[var(--vt-border)]">
            <p className="text-[10px] uppercase text-[var(--vt-text-muted)] tracking-wider">AR Outstanding</p>
            <p className="text-lg font-serif text-[var(--vt-text)]" data-testid="fo-ar-outstanding">
              ${(dashboard.financial_overview.total_ar_outstanding || 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-[var(--vt-bg-card)] rounded-lg p-4 border border-[var(--vt-border)]">
            <p className="text-[10px] uppercase text-[var(--vt-text-muted)] tracking-wider">AP Outstanding</p>
            <p className="text-lg font-serif text-[var(--vt-text)]" data-testid="fo-ap-outstanding">
              ${(dashboard.financial_overview.total_ap_outstanding || 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-[var(--vt-bg-card)] rounded-lg p-4 border border-[var(--vt-border)]">
            <p className="text-[10px] uppercase text-[var(--vt-text-muted)] tracking-wider">Total Received</p>
            <p className="text-lg font-serif text-[var(--vt-sage)]" data-testid="fo-total-received">
              ${(dashboard.financial_overview.total_ar_paid || 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-[var(--vt-bg-card)] rounded-lg p-4 border border-[var(--vt-border)]">
            <p className="text-[10px] uppercase text-[var(--vt-text-muted)] tracking-wider">Overdue</p>
            <p className={`text-lg font-serif ${(dashboard.financial_overview.total_overdue_count || 0) > 0 ? 'text-[var(--vt-terra)]' : 'text-[var(--vt-text-muted)]'}`} data-testid="fo-overdue">
              {dashboard.financial_overview.total_overdue_count || 0} (${(dashboard.financial_overview.total_overdue_amount || 0).toLocaleString()})
            </p>
          </div>
        </motion.div>
      )}

      {/* Global Alerts & Actions - Full Width - RENAMED TO TODAY'S ACTION ITEMS */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="bg-[var(--vt-bg-card)] rounded-2xl p-6 border border-[var(--vt-border)]">
        
        {/* Today's Action Items = Global OS Command Center */}
        <VTCommandCenter 
          onNavigate={(deepLink) => {
            if (deepLink.startsWith('/')) {
              navigate(deepLink);
            } else {
              window.location.href = deepLink;
            }
          }}
          onRefresh={fetchDashboard}
        />
      </motion.div>
    </div>
  );
}
